import i18n from './config';

const DEFAULT_LANGUAGE = 'en';

export function resolveLanguagePreference(
  preference?: string | null,
  supportedLanguages: readonly string[] | false | undefined = i18n.options.supportedLngs
): string {
  const supported = Array.isArray(supportedLanguages)
    ? supportedLanguages.map((lng) => lng.toLowerCase())
    : [];
  const candidate = (preference || DEFAULT_LANGUAGE).toLowerCase();

  if (supported.length > 0) {
    if (supported.includes(candidate)) {
      return candidate;
    }
    const base = candidate.split('-')[0];
    if (base && supported.includes(base)) {
      return base;
    }
  }

  return DEFAULT_LANGUAGE;
}

/** Set when Team Portal confirms a user-chosen locale (avoid DB default `"en"` clobbering `navigator`). */
const EXPLICIT_LOCALE_STORAGE_KEY = 'creately:userLocaleExplicit';

export function persistExplicitUserLocale(raw: string) {
  const next = resolveLanguagePreference(raw);
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(EXPLICIT_LOCALE_STORAGE_KEY, next);
  } catch {
    /* ignore quota / privacy mode */
  }
}

export function readExplicitUserLocale(): string | null | undefined  {
  try {
    if (typeof localStorage === 'undefined') return null; /* null when no localStorage */
    const v = localStorage.getItem(EXPLICIT_LOCALE_STORAGE_KEY);
    return v && v.trim().length > 0 ? v.toLowerCase() : undefined; /* undefined when empty */
  } catch {
    return null; /* null when error */
  }
}

export async function applyLanguagePreference(preference?: string | null) {
  const next = resolveLanguagePreference(preference);
  if (i18n.language === next) return;
  try {
    await i18n.changeLanguage(next);
  } catch {
    return;
  }
}

export function buildLocalizedSitePath(pagePath: string, preference?: string | null): string {
  const next = resolveLanguagePreference(preference);
  const normalizedPagePath = pagePath.replace(/^\/+/, '');

  if (next === DEFAULT_LANGUAGE) {
    return normalizedPagePath;
  }

  return `${next}/${normalizedPagePath}`;
}

export function buildLocalizedSiteUrl(
  siteUrl: string,
  pagePath: string,
  preference?: string | null,
): string {
  const normalizedSiteUrl = siteUrl.endsWith('/') ? siteUrl : `${siteUrl}/`;
  return new URL(buildLocalizedSitePath(pagePath, preference), normalizedSiteUrl).toString();
}

